import { openDialog } from '@patrianna/dialog-manager'
import cx from 'classnames'
import { format, isToday } from 'date-fns'
import { useEffect, useRef, type MutableRefObject } from 'react'

import Button from 'components/Button'
import { IS_BRAND_PULSZ } from 'config/brands'
import { useAppSelector, useAppDispatch } from 'src/store/hooks'
import { sweepstakeEnabledSelector } from 'store/modules/appConfig/selectors'
import { getWheelCardConfig, getWheelMode } from 'store/modules/brandConfig/selectors'
import { getUserFreeSpins } from 'store/modules/slotGameFlow/actions'

import wheelCardConfig, { WHEEL_MODES } from './config'
import { getDisplayRewardConfig, getButtonText } from './config/rewardSectionConfig'
import { useCanvas, useWheelApi, useShowFreeSpinsClaimSnackbar, useHandlePurchaseOfferClaim } from './hooks'
import classes from './styles.module.scss'
import { t, isFreeSpinsWin, isPurchaseOfferWin } from './utils'

type WheelCardProps = {
  onContentBtnClick: () => void
  celebrationNodeRef?: MutableRefObject<HTMLCanvasElement>
  onWheelStateChange?: (isFreeSpinsWin: boolean, messageKey?: string) => void
  dataTestKey?: string
  wheelLocation?: 'wheel_of_winners' | 'promo_wheel'
}

const errorMessageClassMapping: Record<WHEEL_MODES, string> = {
  [WHEEL_MODES.default]: '',
  [WHEEL_MODES.halloween]: '',
  [WHEEL_MODES.christmas]: '',
  [WHEEL_MODES.saint_valentine]: '',
  [WHEEL_MODES.easter]: '',
  [WHEEL_MODES.summer]: classes.errorText__pink2,
  [WHEEL_MODES.summer_spinner]: classes.errorText__pink2,
  [WHEEL_MODES.weekend_super_spinner]: classes.errorText__black,
  [WHEEL_MODES.loyalty_legend]: '',
  [WHEEL_MODES.loyalty_icon]: '',
  [WHEEL_MODES.star_spangled]: '',
}

export default function WheelCard(props: WheelCardProps) {
  const {
    onContentBtnClick,
    celebrationNodeRef: propCelebrationNodeRef,
    onWheelStateChange,
    wheelLocation = 'wheel_of_winners',
  } = props
  const wheelNodeRef = useRef<HTMLCanvasElement>(null)
  const celebrationNodeRef = useRef<HTMLCanvasElement>(null)
  const config = useAppSelector(getWheelCardConfig)
  const translate = t(config)
  const { animated, error, nextSpinDate, sectionDetails, messageKey, isTopPrizeWon, ...wheelApi } = useWheelApi()
  const wheelMode = useAppSelector(getWheelMode)
  const hasFreeSpinsWin = isFreeSpinsWin(sectionDetails)
  const hasPurchaseOfferWin = isPurchaseOfferWin(sectionDetails)
  const displayRewardConfig = getDisplayRewardConfig(
    sectionDetails,
    hasFreeSpinsWin,
    hasPurchaseOfferWin,
    messageKey,
    isTopPrizeWon,
  )
  const buttonText = getButtonText(sectionDetails, hasFreeSpinsWin, hasPurchaseOfferWin, messageKey)
  const notifyFreeSpinsClaimIssue = useShowFreeSpinsClaimSnackbar()
  const handlePurchaseOfferClaim = useHandlePurchaseOfferClaim()
  const dispatch = useAppDispatch()
  const isScEnabled = useAppSelector(sweepstakeEnabledSelector)

  // Notify parent of wheel state changes
  useEffect(() => {
    if (onWheelStateChange) {
      onWheelStateChange(hasFreeSpinsWin, messageKey)
    }
  }, [messageKey, onWheelStateChange, hasFreeSpinsWin])

  const handleClaimClick = () => {
    onContentBtnClick()

    if (hasFreeSpinsWin) {
      if (!messageKey) {
        dispatch(getUserFreeSpins(isScEnabled))
        openDialog({ dialogName: 'FREE_SPINS_DIALOG' })
      }

      notifyFreeSpinsClaimIssue(messageKey)
    }

    if (hasPurchaseOfferWin) {
      handlePurchaseOfferClaim(messageKey)
    }
  }

  useCanvas({
    ...wheelApi,
    wheelNodeRef,
    celebrationNodeRef: propCelebrationNodeRef || celebrationNodeRef,
    wheelLocation,
  })

  return (
    <>
      {!propCelebrationNodeRef && <canvas ref={celebrationNodeRef} className={classes.celebration} />}

      <div
        className={classes.wheelWrapper}
        data-test={`wheel-of-winners${props.dataTestKey ? `-${props.dataTestKey}` : ''}`}
      >
        {error ? (
          <div className={classes.wheelErorTextWrapper}>
            <p
              className={cx(
                classes.errorText,
                wheelMode && Object.values(WHEEL_MODES).includes(wheelMode as WHEEL_MODES)
                  ? errorMessageClassMapping[wheelMode as WHEEL_MODES]
                  : errorMessageClassMapping[WHEEL_MODES.default],
              )}
            >
              {translate('error_msg_line1')}
              <br />
              {translate('error_msg_line2')}
            </p>
          </div>
        ) : (
          <canvas ref={wheelNodeRef} width={320} height={350} className={classes.wheel} />
        )}

        {!error && animated && (
          <div className={classes.contentWrapper}>
            {sectionDetails ? (
              <div className={classes.rewardSection}>
                <div
                  className={cx(classes.rewardSectionTitle, {
                    [classes.rewardSectionTitleSmall]: hasPurchaseOfferWin,
                    [classes.rewardSectionTitlePulsz]: IS_BRAND_PULSZ,
                  })}
                >
                  {displayRewardConfig.title}
                </div>
                <div
                  className={cx(classes.rewardSectionSubtitle, {
                    [classes.rewardSectionSubtitlePulsz]: IS_BRAND_PULSZ,
                  })}
                >
                  {displayRewardConfig.subtitle}
                </div>
              </div>
            ) : (
              <img
                src={config?.images.congrats ?? wheelCardConfig.images.congrats}
                alt={translate('headline_claim_alt')}
                className={classes.contentHeadline}
                width={242}
                height={80}
              />
            )}
            <Button color='SC' className={classes.contentBtn} onClick={handleClaimClick}>
              {buttonText}
            </Button>
            <div className={classes.nextSpinText}>
              {nextSpinDate && (
                <>
                  <span>{translate('next_spin')}</span>
                  <span>
                    {format(nextSpinDate, 'p')} {isToday(nextSpinDate) ? 'TODAY' : 'TOMORROW'}
                  </span>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  )
}
